import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Trash2, Plus, Lock, RefreshCw, LogOut, ShieldAlert } from "lucide-react";

const inputCls =
  "border border-[#311B4D] rounded-md bg-[#0F0616] text-[#FDFBF7] placeholder:text-[#6b5a86] focus-visible:ring-1 focus-visible:ring-[#D4AF37]";

const statusColors = {
  new: "text-[#EAD178] border-[#D4AF37]/40",
  contacted: "text-[#A894C2] border-[#311B4D]",
  closed: "text-[#6b5a86] border-[#311B4D]",
};

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.9 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34.5 6.1 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/>
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 12 24 12c3 0 5.8 1.1 7.9 3l5.7-5.7C34.5 6.1 29.5 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
    <path fill="#4CAF50" d="M24 44c5.2 0 10-2 13.6-5.2l-6.3-5.3C29.2 35 26.7 36 24 36c-5.3 0-9.7-3.1-11.3-7.5l-6.5 5C9.6 39.6 16.2 44 24 44z"/>
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.3 5.3C41.4 36.3 44 30.7 44 24c0-1.3-.1-2.3-.4-3.5z"/>
  </svg>
);

export default function Admin() {
  const { user, loading, login, logout } = useAuth();
  const [enquiries, setEnquiries] = useState([]);
  const [products, setProducts] = useState([]);

  const isAdmin = user && user.is_admin;

  const load = useCallback(async () => {
    try {
      const [enqRes, prodRes] = await Promise.all([
        fetch(`${API}/admin/enquiries`, { credentials: "include" }),
        fetch(`${API}/products`),
      ]);
      if (enqRes.ok) setEnquiries(await enqRes.json());
      if (prodRes.ok) setProducts(await prodRes.json());
    } catch {
      toast.error("Failed to load dashboard");
    }
  }, []);

  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin, load]);

  const setStatus = async (id, status) => {
    try {
      const r = await fetch(`${API}/admin/enquiries/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ status }),
      });
      if (!r.ok) throw new Error();
      setEnquiries((prev) => prev.map((x) => (x.id === id ? { ...x, status } : x)));
    } catch {
      toast.error("Update failed");
    }
  };

  const emptyProduct = {
    name: "",
    category: "Boxes",
    tagline: "",
    price_from: 0,
    image: "",
    size: "square",
    order: 99,
  };
  const [draft, setDraft] = useState(emptyProduct);

  const addProduct = async (e) => {
    e.preventDefault();
    if (!draft.name || !draft.image) {
      toast.error("Name and image URL required");
      return;
    }
    try {
      const r = await fetch(`${API}/products`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ ...draft, price_from: Number(draft.price_from) || 0 }),
      });
      if (!r.ok) throw new Error();
      const np = await r.json();
      setProducts((p) => [...p, np]);
      setDraft(emptyProduct);
      toast.success("Product added");
    } catch {
      toast.error("Failed to add");
    }
  };

  const removeProduct = async (id) => {
    try {
      const r = await fetch(`${API}/products/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!r.ok) throw new Error();
      setProducts((p) => p.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Failed to delete");
    }
  };

  // ---- Loading ----
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0F0616]" data-testid="admin-loading">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-[#311B4D] border-t-[#D4AF37]" />
      </div>
    );
  }

  // ---- Not signed in ----
  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center px-6" data-testid="admin-login">
        <div className="w-full max-w-sm rounded-2xl border border-[#311B4D] bg-[#140823] p-10 text-center">
          <div className="mb-6 flex items-center justify-center gap-3 text-gold">
            <Lock size={20} />
            <span className="font-serif text-2xl">Elixz Admin</span>
          </div>
          <p className="mb-8 text-sm text-[#A894C2]">
            Sign in with your authorised Google account to manage enquiries & products.
          </p>
          <button
            onClick={() => login("/admin")}
            data-testid="admin-google-login"
            className="flex w-full items-center justify-center gap-3 rounded-full bg-white py-3 text-sm font-semibold text-[#1A0B2E] transition-transform duration-300 hover:scale-[1.02]"
          >
            <GoogleIcon /> Sign in with Google
          </button>
          <a href="/" className="mt-6 block text-xs text-[#6b5a86] hover:text-gold">
            &larr; Back to site
          </a>
        </div>
      </div>
    );
  }

  // ---- Signed in but not admin ----
  if (!isAdmin) {
    return (
      <div className="flex min-h-screen items-center justify-center px-6" data-testid="admin-unauthorized">
        <div className="w-full max-w-sm rounded-2xl border border-[#311B4D] bg-[#140823] p-10 text-center">
          <ShieldAlert size={30} className="mx-auto mb-4 text-[#EAD178]" />
          <h2 className="font-serif text-2xl text-[#FDFBF7]">Not authorised</h2>
          <p className="mt-3 text-sm text-[#A894C2]">
            {user.email} does not have admin access. Please sign in with the approved account.
          </p>
          <button
            onClick={logout}
            className="mt-6 flex w-full items-center justify-center gap-2 rounded-full border border-[#311B4D] py-3 text-sm text-[#A894C2] hover:text-gold"
            data-testid="admin-logout-unauth"
          >
            <LogOut size={16} /> Sign out
          </button>
          <a href="/" className="mt-4 block text-xs text-[#6b5a86] hover:text-gold">
            &larr; Back to site
          </a>
        </div>
      </div>
    );
  }

  // ---- Admin dashboard ----
  return (
    <div className="min-h-screen px-6 py-10 md:px-12" data-testid="admin-dashboard">
      <div className="mx-auto max-w-[1200px]">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h1 className="font-serif text-3xl gold-gradient-text">Elixz Dashboard</h1>
          <div className="flex items-center gap-3">
            <span className="hidden text-xs text-[#A894C2] sm:inline">{user.email}</span>
            <button
              onClick={load}
              className="flex items-center gap-2 rounded-full border border-[#311B4D] px-4 py-2 text-xs text-[#A894C2] hover:text-gold"
              data-testid="admin-refresh"
            >
              <RefreshCw size={14} /> Refresh
            </button>
            <button
              onClick={logout}
              className="flex items-center gap-2 rounded-full border border-[#311B4D] px-4 py-2 text-xs text-[#A894C2] hover:text-gold"
              data-testid="admin-logout"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>

        <Tabs defaultValue="enquiries" className="mt-8">
          <TabsList className="bg-[#140823]">
            <TabsTrigger value="enquiries" data-testid="tab-enquiries">
              Enquiries ({enquiries.length})
            </TabsTrigger>
            <TabsTrigger value="products" data-testid="tab-products">
              Products ({products.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="enquiries" className="mt-6">
            {enquiries.length === 0 ? (
              <p className="text-[#A894C2]">No enquiries yet.</p>
            ) : (
              <div className="space-y-4">
                {enquiries.map((e) => (
                  <div
                    key={e.id}
                    className="rounded-xl border border-[#311B4D] bg-[#140823] p-6"
                    data-testid="enquiry-row"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div>
                        <div className="font-serif text-xl text-[#FDFBF7]">
                          {e.name}{" "}
                          <span className="text-sm text-[#A894C2]">&middot; {e.phone}</span>
                          {e.type === "bulk" && (
                            <span className="ml-2 rounded-full border border-[#D4AF37]/50 px-2 py-0.5 text-[10px] uppercase tracking-wider text-gold">
                              Bulk / Corporate
                            </span>
                          )}
                        </div>
                        <div className="mt-1 text-sm text-[#A894C2]">
                          {e.company && `${e.company} · `}
                          {e.occasion || "—"} &middot; Qty: {e.quantity || "—"}
                          {e.event_date && ` · Event: ${e.event_date}`}
                          {e.budget && ` · Budget: ${e.budget}`}
                          {e.email && ` · ${e.email}`}
                        </div>
                        {e.message && (
                          <p className="mt-3 max-w-2xl text-sm text-[#FDFBF7]">{e.message}</p>
                        )}
                        <div className="mt-2 text-xs text-[#6b5a86]">
                          {new Date(e.created_at).toLocaleString()}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {["new", "contacted", "closed"].map((s) => (
                          <button
                            key={s}
                            onClick={() => setStatus(e.id, s)}
                            className={`rounded-full border px-3 py-1 text-xs capitalize transition-colors ${
                              e.status === s
                                ? statusColors[s]
                                : "border-[#311B4D] text-[#6b5a86] hover:text-[#A894C2]"
                            }`}
                            data-testid={`status-${s}`}
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="products" className="mt-6">
            <form
              onSubmit={addProduct}
              className="mb-8 grid gap-4 rounded-xl border border-[#311B4D] bg-[#140823] p-6 md:grid-cols-2"
              data-testid="product-form"
            >
              <div className="grid gap-2">
                <Label className="text-xs text-[#A894C2]">Name</Label>
                <Input
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  className={inputCls}
                  data-testid="product-name"
                />
              </div>
              <div className="grid gap-2">
                <Label className="text-xs text-[#A894C2]">Category</Label>
                <Input
                  value={draft.category}
                  onChange={(e) => setDraft({ ...draft, category: e.target.value })}
                  className={inputCls}
                />
              </div>
              <div className="grid gap-2">
                <Label className="text-xs text-[#A894C2]">Tagline</Label>
                <Input
                  value={draft.tagline}
                  onChange={(e) => setDraft({ ...draft, tagline: e.target.value })}
                  className={inputCls}
                />
              </div>
              <div className="grid gap-2">
                <Label className="text-xs text-[#A894C2]">Price From (₹)</Label>
                <Input
                  type="number"
                  value={draft.price_from}
                  onChange={(e) => setDraft({ ...draft, price_from: e.target.value })}
                  className={inputCls}
                />
              </div>
              <div className="grid gap-2 md:col-span-2">
                <Label className="text-xs text-[#A894C2]">Image URL</Label>
                <Input
                  value={draft.image}
                  onChange={(e) => setDraft({ ...draft, image: e.target.value })}
                  className={inputCls}
                  data-testid="product-image"
                />
              </div>
              <button
                type="submit"
                className="flex items-center justify-center gap-2 rounded-full bg-gold py-3 text-sm font-semibold text-[#0F0616] md:col-span-2"
                data-testid="product-add-btn"
              >
                <Plus size={16} /> Add Product
              </button>
            </form>

            <div className="grid gap-4 md:grid-cols-2">
              {products.map((p) => (
                <div
                  key={p.id}
                  className="flex items-center gap-4 rounded-xl border border-[#311B4D] bg-[#140823] p-4"
                  data-testid="product-row"
                >
                  <img src={p.image} alt={p.name} className="h-16 w-16 rounded-lg object-cover" />
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-[#FDFBF7]">{p.name}</div>
                    <div className="text-xs text-[#A894C2]">
                      {p.category} &middot; ₹{p.price_from}
                    </div>
                  </div>
                  <button
                    onClick={() => removeProduct(p.id)}
                    className="text-[#A894C2] hover:text-red-400"
                    data-testid="product-delete"
                    aria-label="Delete"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
